import {assignments, orgs} from './tellusBadgesRegistry'
import type {Badge, TellusBadgeType} from './badgeTypes'

type ActorLike = {
  did?: string
  handle?: string
  verification?: {
    verifiedStatus?: string
    trustedVerifierStatus?: string
  }
}

type TellusAssignment = (typeof assignments)[number]
type TellusOrg = (typeof orgs)[number]

const TELLUS_BADGE_LABELS: Record<TellusBadgeType, string> = {
  TELLUS_VERIFIED: 'Verified by Tellus',
  TELLUS_STAFF: 'Tellus Staff',
  ORG_OFFICIAL: 'Official Organization',
  ORG_MEMBER: 'Member of',
}

const BADGE_ORDER: Record<string, number> = {
  BLUESKY_VERIFIED: 0,
  BLUESKY_TRUSTED_VERIFIER: 1,
  BLUESKY_OTHER: 2,
  TELLUS_VERIFIED: 3,
  ORG_OFFICIAL: 4,
  TELLUS_STAFF: 5,
  ORG_MEMBER: 6,
}

export function getTellusAssignmentForDid(
  did?: string,
): TellusAssignment | undefined {
  if (!did) return undefined
  return assignments.find(entry => entry.did === did)
}

export function getOrgById(orgId?: string): TellusOrg | undefined {
  if (!orgId) return undefined
  return orgs.find(org => org.id === orgId)
}

export function getBlueskyBadges(actorOrProfile?: ActorLike): Badge[] {
  if (!actorOrProfile?.verification) return []

  const badges: Badge[] = []
  const {verifiedStatus, trustedVerifierStatus} = actorOrProfile.verification

  const isVerified =
    verifiedStatus === 'valid' || trustedVerifierStatus === 'valid'

  if (isVerified) {
    badges.push({
      source: 'bluesky',
      type: 'BLUESKY_VERIFIED',
      label: 'Verified on Bluesky',
      reason: 'Verified on Bluesky',
    })
  }

  if (trustedVerifierStatus === 'valid') {
    badges.push({
      source: 'bluesky',
      type: 'BLUESKY_TRUSTED_VERIFIER',
      label: 'Trusted verifier on Bluesky',
      reason: 'Trusted verifier on Bluesky',
    })
  }

  return badges
}

export function getBadgesForActor(actorOrProfile?: ActorLike): Badge[] {
  if (!actorOrProfile) return []

  const badges: Badge[] = []
  const blueskyBadges = getBlueskyBadges(actorOrProfile)
  const assignment = actorOrProfile.did
    ? getTellusAssignmentForDid(actorOrProfile.did)
    : undefined
  const hasBlueskyVerified = blueskyBadges.some(
    badge => badge.type === 'BLUESKY_VERIFIED',
  )

  badges.push(...blueskyBadges)

  if (hasBlueskyVerified) {
    badges.push({
      source: 'tellus',
      type: 'TELLUS_VERIFIED',
      label: TELLUS_BADGE_LABELS.TELLUS_VERIFIED,
      reason: 'Auto: account verified on Bluesky',
    })
  } else if (assignment?.badges.includes('TELLUS_VERIFIED')) {
    badges.push({
      source: 'tellus',
      type: 'TELLUS_VERIFIED',
      label: TELLUS_BADGE_LABELS.TELLUS_VERIFIED,
      issuedAt: assignment.issuedAt,
      reason: 'Issued by Tellus team',
    })
  }

  if (assignment?.badges.includes('TELLUS_STAFF')) {
    badges.push({
      source: 'tellus',
      type: 'TELLUS_STAFF',
      label: TELLUS_BADGE_LABELS.TELLUS_STAFF,
      issuedAt: assignment.issuedAt,
      reason: 'Issued by Tellus team',
    })
  }

  if (assignment?.badges.includes('ORG_OFFICIAL')) {
    const org = getOrgById(assignment.orgId)
    badges.push({
      source: 'tellus',
      type: 'ORG_OFFICIAL',
      label: TELLUS_BADGE_LABELS.ORG_OFFICIAL,
      issuedAt: assignment.issuedAt,
      reason: 'Issued by Tellus team',
      org: org
        ? {id: org.id, name: org.name, didOrHandle: org.didOrHandle}
        : undefined,
    })
  }

  if (assignment?.badges.includes('ORG_MEMBER')) {
    const org = getOrgById(assignment.orgId)
    badges.push({
      source: 'tellus',
      type: 'ORG_MEMBER',
      label: org
        ? `${TELLUS_BADGE_LABELS.ORG_MEMBER} ${org.name}`
        : TELLUS_BADGE_LABELS.ORG_MEMBER,
      issuedAt: assignment.issuedAt,
      reason: 'Issued by Tellus team',
      org: org
        ? {id: org.id, name: org.name, didOrHandle: org.didOrHandle}
        : undefined,
    })
  }

  const seen = new Set<string>()
  const deduped = badges.filter(badge => {
    const key = `${badge.source}:${badge.type}:${badge.org?.id || ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return deduped.sort((a, b) => {
    const aOrder = BADGE_ORDER[a.type] ?? 999
    const bOrder = BADGE_ORDER[b.type] ?? 999
    return aOrder - bOrder
  })
}
